import { parseAsStringLiteral, useQueryState } from 'nuqs'
import { createContext, PropsWithChildren, useContext, useMemo } from 'react'

import { objectValues } from '~/utils/object-values'

export const ACCOUNT_SECTIONS = {
  ORDERS: 'orders',
  ACCOUNT_INFORMATIONS: 'account_informations',
  ADDRESS_BOOK: 'address_book',
  RETURN_PRODUCT: 'return_product',
} as const

type AccountSection = (typeof ACCOUNT_SECTIONS)[keyof typeof ACCOUNT_SECTIONS]

type AccountNavigationContextValue = {
  currentSection?: AccountSection
  setCurrentSection: (section: AccountSection) => void
}

const AccountNavigationContext = createContext<AccountNavigationContextValue>({
  currentSection: undefined,
  setCurrentSection: () => {},
})

export function useAccountNavigation() {
  const context = useContext(AccountNavigationContext)

  if (!context) {
    throw new Error(
      'useAccountNavigation must be used within a AccountNavigationProvider',
    )
  }

  return context
}

export function AccountNavigationProvider({ children }: PropsWithChildren) {
  const [currentSection, setCurrentSection] = useQueryState(
    'section',
    parseAsStringLiteral(objectValues(ACCOUNT_SECTIONS)).withDefault('orders'),
  )

  const value = useMemo(() => {
    return { currentSection, setCurrentSection }
  }, [currentSection])

  return (
    <AccountNavigationContext.Provider value={value}>
      {children}
    </AccountNavigationContext.Provider>
  )
}
